/**
 * ADMIN-REVIEW L5 step 1: one place for the classic (v1) / modern (v2) view preference.
 *
 * Before this, an unset preference meant v2 for the customer dashboard and order form but v1 for
 * the admin areas and the sign-in page, so a first-time user saw a mix. Everything now reads the
 * preference here, and an unset (or unreadable) value means DEFAULT_VIEW_PREF everywhere.
 * The localStorage key keeps its historical name so existing choices carry over.
 */
export type ViewPref = 'v1' | 'v2';

export const VIEW_PREF_KEY = 'admin_layout_version';
export const DEFAULT_VIEW_PREF: ViewPref = 'v2';

export function getViewPref(): ViewPref {
  try {
    const v = localStorage.getItem(VIEW_PREF_KEY);
    return v === 'v1' || v === 'v2' ? v : DEFAULT_VIEW_PREF;
  } catch {
    return DEFAULT_VIEW_PREF;
  }
}

export function setViewPref(v: ViewPref): void {
  try { localStorage.setItem(VIEW_PREF_KEY, v); } catch { /* private mode: keep the default */ }
}

export const isModernView = (): boolean => getViewPref() === 'v2';

/** Flip the preference and return the new value. */
export function toggleViewPref(): ViewPref {
  const next: ViewPref = getViewPref() === 'v2' ? 'v1' : 'v2';
  setViewPref(next);
  return next;
}
